import type {
  AuthResponse,
  Article,
  Category,
  Tag,
  PaginatedResponse,
  ArticleFilters,
  DashboardStats,
  AnalyticsData,
  ArticleVersion,
  User,
  Redirect,
} from "./types"

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

class ApiClient {
  private getToken(): string | null {
    if (typeof window === "undefined") return null
    return localStorage.getItem("cms_token")
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken()
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    }

    if (token) {
      headers["Authorization"] = `Bearer ${token}`
    }

    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    })

    if (res.status === 401) {
      if (typeof window !== "undefined") {
        localStorage.removeItem("cms_token")
        localStorage.removeItem("cms_user")
        window.location.href = "/login"
      }
      throw new Error("Unauthorized")
    }

    if (res.status === 403) {
      throw new Error("Access Denied")
    }

    if (!res.ok) {
      const error = await res.json().catch(() => ({ detail: "An error occurred" }))
      throw new Error(error.detail || error.message || "Request failed")
    }

    if (res.status === 204) return {} as T
    return res.json()
  }

  // Auth
  auth = {
    login: (email_or_username: string, password: string) =>
      this.request<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email_or_username, password }),
      }),
    register: (data: { name: string; email: string; password: string; role: string }) =>
      this.request<User>("/auth/register", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  }

  // Articles
  articles = {
    list: (filters: ArticleFilters = {}) => {
      const params = new URLSearchParams()
      if (filters.status && filters.status !== "all") params.set("status", filters.status)
      if (filters.category_id) params.set("category_id", filters.category_id)
      if (filters.author_id) params.set("author_id", filters.author_id)
      if (filters.search) params.set("search", filters.search)
      if (filters.page) params.set("page", String(filters.page))
      if (filters.page_size) params.set("page_size", String(filters.page_size))
      return this.request<PaginatedResponse<Article>>(`/articles?${params}`)
    },
    get: (id: string) => this.request<Article>(`/articles/${id}`),
    create: (data: Partial<Article>) =>
      this.request<Article>("/articles", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<Article>) =>
      this.request<Article>(`/articles/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string, redirectTo?: string) => {
      const url = redirectTo 
        ? `/articles/${id}?redirect_to=${encodeURIComponent(redirectTo)}`
        : `/articles/${id}`
      return this.request<void>(url, { method: "DELETE" })
    },
    autoSave: (id: string, data: Partial<Article>) =>
      this.request<Article>(`/articles/${id}/auto-save`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    versions: (id: string) =>
      this.request<ArticleVersion[]>(`/articles/${id}/versions`),
    submitForReview: (id: string) =>
      this.request<Article>(`/articles/${id}/submit`, { method: "POST" }),
    approve: (id: string) =>
      this.request<Article>(`/articles/${id}/approve`, { method: "POST" }),
    reject: (id: string, reason?: string) =>
      this.request<Article>(`/articles/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    publish: (id: string) =>
      this.request<Article>(`/articles/${id}/publish`, { method: "POST" }),
    schedule: (id: string, scheduled_at: string) =>
      this.request<Article>(`/articles/${id}/schedule`, {
        method: "POST",
        body: JSON.stringify({ scheduled_at }),
      }),
    duplicate: (id: string) =>
      this.request<Article>(`/articles/${id}/duplicate`, { method: "POST" }),
    uploadImage: async (file: File) => {
      const token = this.getToken()
      const formData = new FormData()
      formData.append("file", file)
      const res = await fetch(`${API_BASE}/upload/image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: "Upload failed" }))
        throw new Error(error.detail || "Upload failed")
      }
      const result = await res.json()
      // If URL is relative, make it absolute
      const url = result.url.startsWith("http") 
        ? result.url 
        : `${API_BASE.replace("/api", "")}${result.url}`
      return url
    },
    uploadInputImage: async (file: File) => {
      const token = this.getToken()
      const formData = new FormData()
      formData.append("file", file)
      const res = await fetch(`${API_BASE}/upload/input-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: "Upload failed" }))
        throw new Error(error.detail || "Upload failed")
      }
      const result = await res.json()
      // Make URL absolute if it's relative
      if (result.url && !result.url.startsWith("http")) {
        result.url = `${API_BASE.replace("/api", "")}${result.url}`
      }
      return result
    },
  }

  // Categories
  categories = {
    list: () => this.request<Category[]>("/categories"),
    create: (data: Partial<Category>) =>
      this.request<Category>("/categories", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<Category>) =>
      this.request<Category>(`/categories/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      this.request<void>(`/categories/${id}`, { method: "DELETE" }),
  }

  // Tags
  tags = {
    list: (featured_only = false, search?: string, limit?: number) => {
      const params = new URLSearchParams()
      params.set("featured_only", String(featured_only))
      if (search?.trim()) params.set("search", search.trim())
      if (limit) params.set("limit", String(limit))
      return this.request<Tag[]>(`/tags?${params}`)
    },
    create: (data: Partial<Tag>) =>
      this.request<Tag>("/tags", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<Tag>) =>
      this.request<Tag>(`/tags/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      this.request<void>(`/tags/${id}`, { method: "DELETE" }),
    bulkFeature: (ids: string[]) =>
      this.request<void>("/tags/bulk-feature", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
  }

  // Website Settings
  websiteSettings = {
    get: () =>
      this.request<{
        id: number
        header_logo?: string
        footer_logo?: string
        primary_color?: string
        secondary_color?: string
        accent_color?: string
      }>("/website-settings"),
    update: (data: {
      header_logo?: string
      footer_logo?: string
      primary_color?: string
      secondary_color?: string
      accent_color?: string
    }) =>
      this.request<{
        id: number
        header_logo?: string
        footer_logo?: string
        primary_color?: string
        secondary_color?: string
        accent_color?: string
      }>("/website-settings", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
  }

  // Analytics
  analytics = {
    dashboard: (days = 30) =>
      this.request<DashboardStats>(`/analytics/dashboard?days=${days}`),
    full: (days = 30) =>
      this.request<AnalyticsData>(`/analytics/full?days=${days}`),
    article: (id: string, days = 30) =>
      this.request<AnalyticsData>(`/analytics/articles/${id}?days=${days}`),
  }

  // Users
  users = {
    list: () => this.request<User[]>("/users"),
    update: (id: string, data: Partial<User>) =>
      this.request<User>(`/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      this.request<void>(`/users/${id}`, { method: "DELETE" }),
  }

  // Upload
  upload = {
    getImageByCode: (code: string) =>
      this.request<{ code: string; url: string; title?: string; alt?: string }>(`/upload/image-code/${code}`),
    resolveImageCodesByUrls: (urls: string[]) =>
      this.request<{ images: { code: string; url: string; title?: string; alt?: string }[] }>(
        `/upload/image-codes/resolve-urls`,
        {
          method: "POST",
          body: JSON.stringify({ urls }),
        },
      ).then((res) => res.images ?? []),
    updateImageCodeMetadata: (code: string, metadata: { title?: string; alt?: string }) =>
      this.request<{ code: string; url: string; title?: string; alt?: string }>(
        `/upload/image-code/${code}/metadata`,
        {
          method: "POST",
          body: JSON.stringify(metadata),
        }
      ),
  }

  // Redirects
  redirects = {
    list: () => this.request<Redirect[]>(`/redirects`),
    update: (id: string, redirectTo: string) =>
      this.request<Redirect>(`/redirects/${id}`, {
        method: "PUT",
        body: JSON.stringify({ redirect_to_slug: redirectTo }),
      }),
    delete: (id: string) =>
      this.request<void>(`/redirects/${id}`, { method: "DELETE" }),
  }
}

export const api = new ApiClient()
